/**
 * Maps UI Composer generation errors to user-facing copy.
 *
 * The API error interceptor (`services/api.ts`) sets `Error.message` to the
 * backend's raw `error` code (e.g. `entitlements_unresolved`) and attaches the
 * full response body to `Error.response`. The raw code is accurate but opaque
 * to a user, so we translate known codes to a clear message while preserving
 * the raw code for debugging.
 *
 * When the backend rejects a request for schema validation, the response body
 * also carries a `validationErrors: [{ field, message }]` array (some code
 * paths emit `{ field, error }` instead — see `innovate-service/src/processes/
 * endpoint.ts` and `innovate-service/src/lib/validator/jsonSchema.ts`).
 * `toComposerError` reads that array off `Error.response` and names the
 * offending fields in the message — e.g. "Request body validation failed:
 * framework, stylingApproach" — so a schema rejection is actionable instead of
 * opaque. This only applies when no friendly translation matches; a known
 * code (like `entitlements_unresolved`) always wins and is never decorated
 * with field names.
 */

export interface ComposerError {
  /** Friendly, user-facing message. */
  message: string;
  /** Raw backend code (or the original message) — kept for debugging. */
  code: string;
}

const FRIENDLY_MESSAGES: Record<string, string> = {
  // `entitlements_unresolved` is a PLATFORM defect (the System account's own
  // entitlements failed to resolve) — the "not a problem with your account"
  // copy applies to this code and ONLY this code (task 465 split). The
  // customer-side no-plan state is `no_active_plan` below.
  entitlements_unresolved:
    "The UI generator is temporarily unavailable — the platform account needs provisioning. " +
    "This isn't a problem with your account; please try again shortly, or contact support if it persists.",
  credential_expired:
    "Your sign-in expired while the service was being built, so nothing was created. " +
    "We've refreshed it — send your message again and it should go through.",
  // `no_active_plan` (task 464/465): the caller's account has no plan, so the
  // request failed closed. The remedy is choosing a plan on Settings →
  // Subscription (task 1026 — "Plan & Billing" is the legacy name of that
  // destination) — never the platform-defect copy above.
  no_active_plan:
    "Your account doesn't have a plan yet, so AI features are disabled. " +
    "Choose a plan on Settings → Subscription to enable them.",
  // The two self-MCP conditions, split in task 877 because one string could only
  // ever describe one of them honestly.
  //
  // `self_mcp_unauthenticated` — Innovate's own tool server ANSWERED and refused:
  // the turn carried no authorization for a node whose connection is deliberately
  // credential-less (the Resource Reviewer, task 755). A platform CONFIGURATION
  // fault. Never say "reaching Innovate" here — Innovate replied, immediately.
  self_mcp_unauthenticated:
    "The review couldn't read your services — this turn carried no authorization for it. " +
    "That's a platform configuration problem, not a problem with your account. " +
    "Please try again, and contact support if it keeps happening.",
  // `self_mcp_unavailable` — Innovate genuinely could not reach its own tool
  // server (DNS, a bad URL, the server down). A deployment fault, and usually
  // transient from the User's side.
  self_mcp_unavailable:
    "Innovate couldn't reach its own tools, so this turn couldn't run. " +
    "Please try again shortly.",
  // `invalid_reply` (task 1140): a step's model reply still broke its output
  // contract after the invalid-reply retries (innovate-service task 1139). The
  // service's own message names the step and the missing fields and is the
  // copy shown (see `invalidReplyMessage`); this is only the fallback for a
  // response that carried no message.
  invalid_reply: 'This step could not produce a valid reply. Please try again.',
};

const INVALID_REPLY_CODE = 'invalid_reply';
/** `invalid_reply: <sentence>` — the code with the service sentence after it. */
const INVALID_REPLY_PREFIXED = /^invalid_reply:\s*(.+)$/s;

/**
 * The service sentence for an `invalid_reply` failure (task 1140), or null when
 * the error is not one. The code reaches the app as the thrown error's
 * `.message` (the interceptor sets it to the body's `error`), with the sentence
 * on the body as `details.message` or `message`; or as one string,
 * `invalid_reply: <sentence>`. With the code but no sentence, the fixed
 * fallback copy applies.
 */
function invalidReplyMessage(err: unknown): string | null {
  const raw = err instanceof Error ? err.message : typeof err === 'string' ? err : '';

  const prefixed = raw.match(INVALID_REPLY_PREFIXED);
  if (prefixed) return prefixed[1].trim();
  if (raw !== INVALID_REPLY_CODE) return null;

  const response =
    typeof err === 'object' && err !== null && 'response' in err
      ? (err as { response?: unknown }).response
      : undefined;
  if (typeof response === 'object' && response !== null) {
    const body = response as Record<string, unknown>;
    const details =
      typeof body.details === 'object' && body.details !== null
        ? (body.details as Record<string, unknown>)
        : undefined;
    const sentence = details?.message ?? body.message;
    if (typeof sentence === 'string' && sentence.trim()) return sentence.trim();
  }
  return FRIENDLY_MESSAGES.invalid_reply;
}

/**
 * Friendly translation for a known backend error code carried on a thrown API
 * error's `.message` (the interceptor sets it to the raw `error` code).
 * Returns null when the code has no translation. Used by surfaces that show
 * inline/toast copy outside the composer flow (runners, endpoint save) so
 * codes like `no_active_plan` never reach the User raw (task 465 sweep).
 */
export function friendlyApiErrorMessage(err: unknown): string | null {
  const code =
    err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  return invalidReplyMessage(err) ?? FRIENDLY_MESSAGES[code] ?? null;
}

/**
 * Signatures of a credential rejection that reached the User as prose rather
 * than as the `credential_expired` code (task 395).
 *
 * The service now fails the run with the bare code, so this is a safety net for
 * one specific path: the builder agent seeing a 401 in a tool result and
 * narrating it into its own `buildResult.error`, which is free-text written by
 * a model and can therefore never be fully relied on to carry a code.
 */
const CREDENTIAL_EXPIRED_PROSE =
  /\b401\b|unauthorized|jwt expired|token expired|invalid or missing authentication/i;

/**
 * Entitlement codes the service emits when it refuses a build (402/403). The
 * builder agent narrates the tool failure into `buildResult.error` as free text
 * — prd showed "endpoint_create failed with [402] no_active_plan" reaching the
 * card verbatim (task 1026) — but the code token itself is a stable string, so
 * a word-boundary match finds it however the agent phrases the sentence.
 */
const NO_ACTIVE_PLAN_CODE = /\bno_active_plan\b/;
const LIMIT_EXCEEDED_CODE = /\blimit_exceeded\b/;
const FEATURE_NOT_ENTITLED_CODE = /\bfeature_not_entitled\b/;

/**
 * The cap and current count off a narrated `limit_exceeded` body
 * (`{ error, limit, current, cap }` — innovate-service `lib/auth/entitlements`),
 * whether the agent pasted the JSON or wrote it out. Absent when the agent
 * dropped the numbers, in which case the copy stays unnumbered.
 */
const CAP_NUMBER = /"?\bcap"?\s*[:=]?\s*(\d+)/i;
const CURRENT_NUMBER = /"?\bcurrent"?\s*[:=]?\s*(\d+)/i;

/** Footer for a failure the User fixes by sending the same message again. */
const RETRY_FOOTER = 'Your design is still here — just send your message again.';
/** Footer for the plan refusals: retrying alone changes nothing. */
const PLAN_FOOTER = 'Your design is still here. Upgrade or free a slot, then send your message again.';

/**
 * A translated build failure: the copy to show, the line under it saying what
 * happens next, and — for a plan refusal — where the remedy lives.
 */
export interface FriendlyBuildError {
  /** Friendly, user-facing explanation of the failure. */
  message: string;
  /** The smaller line under the message: what the User does next. */
  footer: string;
  /** In-app destination for the remedy, when there is one. */
  linkTo?: string;
  /** Label for `linkTo`. */
  linkLabel?: string;
}

const SUBSCRIPTION_LINK = {
  linkTo: '/settings/subscription',
  linkLabel: 'Go to Subscription',
} as const;

/**
 * Translate a build-result failure string into friendly copy.
 *
 * `ComposerBuildResult.error` is not an API error — it is a field the builder
 * agent fills in, so it arrives on a 200 response and never passes through
 * `toComposerError`. Returns null when the text has no friendly translation, in
 * which case the caller should show it as-is.
 *
 * The plan refusals are matched BEFORE the credential prose: both are stable
 * codes the service emits, and an agent narrating a 402/403 can easily use the
 * word "unauthorized" in the same sentence. The `entitlements_unresolved`
 * "temporarily unavailable" copy is deliberately NOT reachable here — that code
 * is a platform defect, not a plan refusal (task 465 split).
 */
export function toFriendlyBuildError(rawError: string | undefined): FriendlyBuildError | null {
  if (!rawError) return null;

  if (NO_ACTIVE_PLAN_CODE.test(rawError)) {
    return {
      message: FRIENDLY_MESSAGES.no_active_plan,
      footer: 'Choose a plan, then send your message again.',
      ...SUBSCRIPTION_LINK,
    };
  }

  // Only one cap can stop a build in its tracks: `max_endpoints`, checked when
  // the builder creates the AI Service (innovate-service `processes/endpoint`).
  // So a `limit_exceeded` in a build result is the AI Services cap whether or
  // not the agent kept the limit key in its sentence.
  if (LIMIT_EXCEEDED_CODE.test(rawError)) {
    const cap = rawError.match(CAP_NUMBER)?.[1];
    const current = rawError.match(CURRENT_NUMBER)?.[1];
    const counts = cap
      ? `Your plan includes ${cap} AI Services and this account already has ${current ?? cap}.`
      : 'This account already has the number of AI Services your plan includes.';
    return {
      message: `${counts} Upgrade on Settings → Subscription to add more, or archive one you no longer need.`,
      footer: PLAN_FOOTER,
      ...SUBSCRIPTION_LINK,
    };
  }

  if (FEATURE_NOT_ENTITLED_CODE.test(rawError)) {
    return {
      message:
        "Your plan doesn't include a capability this service needs. " +
        'See Settings → Subscription for what each plan unlocks.',
      footer: PLAN_FOOTER,
      ...SUBSCRIPTION_LINK,
    };
  }

  if (rawError === 'credential_expired' || CREDENTIAL_EXPIRED_PROSE.test(rawError)) {
    return { message: FRIENDLY_MESSAGES.credential_expired, footer: RETRY_FOOTER };
  }

  return null;
}

/**
 * Best-effort extraction of the offending field names from a thrown error's
 * `.response` (attached by the API interceptor as `enhancedError.response =
 * error.response?.data`). Returns an empty array for any shape that isn't a
 * `validationErrors: [...]` array — never throws, since `.response` is
 * untyped data straight from the network.
 */
function extractValidationFields(err: unknown): string[] {
  if (typeof err !== 'object' || err === null || !('response' in err)) return [];

  const response = (err as { response?: unknown }).response;
  if (typeof response !== 'object' || response === null) return [];

  const validationErrors = (response as Record<string, unknown>).validationErrors;
  if (!Array.isArray(validationErrors)) return [];

  const fields: string[] = [];
  for (const entry of validationErrors) {
    if (typeof entry !== 'object' || entry === null) continue;
    const record = entry as Record<string, unknown>;
    // Prefer the field name; fall back to whichever text key is present
    // (`message` from the schema-contract branch, `error` from others) so an
    // entry without a `field` still contributes something readable.
    const label =
      (typeof record.field === 'string' && record.field) ||
      (typeof record.message === 'string' && record.message) ||
      (typeof record.error === 'string' && record.error) ||
      undefined;
    if (label && !fields.includes(label)) fields.push(label);
  }
  return fields;
}

/**
 * Derive a friendly message + raw code from a thrown generation error. Unknown
 * codes fall back to the original message so nothing is ever swallowed.
 */
export function toComposerError(err: unknown): ComposerError {
  const raw =
    err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  const code = raw || 'unknown_error';

  const invalidReply = invalidReplyMessage(err);
  if (invalidReply) {
    return { message: invalidReply, code: INVALID_REPLY_CODE };
  }

  const friendly = FRIENDLY_MESSAGES[code];
  if (friendly) {
    return { message: friendly, code };
  }

  const fields = extractValidationFields(err);
  const message =
    fields.length > 0
      ? `${raw || 'Generation failed'}: ${fields.join(', ')}`
      : raw || 'Generation failed.';

  return { message, code };
}

/** True when a friendly translation was applied (message differs from the raw code). */
export function hasFriendlyMessage(err: ComposerError): boolean {
  return err.message !== err.code;
}
